import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Button,
  message,
  Space,
  Radio
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import ImageUpload from '../../components/ImageUpload';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const PopupForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPopupDetail();
    }
  }, [id]);

  const fetchPopupDetail = async () => {
    try {
      const response = await api.get(`/admin/popups/${id}`);
      if (response.code === 200) {
        const popup = response.data;
        form.setFieldsValue({
          title: popup.title,
          image_url: popup.image_url,
          content: popup.content,
          time_range: [dayjs(popup.start_time), dayjs(popup.end_time)],
          frequency_type: popup.frequency_type,
          frequency_limit: popup.frequency_limit,
          condition_field: popup.condition_field,
          condition_value: popup.condition_value,
          link_type: popup.link_type,
          link_url: popup.link_url,
          priority: popup.priority,
          status: popup.status === 'active',
        });
      } else {
        message.error(response.message || '获取弹窗详情失败');
      }
    } catch (error) {
      message.error('获取弹窗详情失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const params = {
        title: values.title,
        image_url: values.image_url || null,
        content: values.content || null,
        start_time: values.time_range[0].format('YYYY-MM-DD HH:mm:ss'),
        end_time: values.time_range[1].format('YYYY-MM-DD HH:mm:ss'),
        frequency_type: values.frequency_type,
        frequency_limit: values.frequency_limit || null,
        condition_field: values.condition_field || null,
        condition_value: values.condition_value || null,
        link_type: values.link_type,
        link_url: values.link_url || null,
        priority: values.priority || 0,
        status: values.status ? 'active' : 'inactive',
      };

      let response;
      if (id) {
        // 更新
        response = await api.put(`/admin/popups/${id}`, params);
      } else {
        // 创建
        response = await api.post('/admin/popups', params);
      }

      if (response.code === 200) {
        message.success(id ? '更新成功' : '创建成功');
        navigate('/popups');
      } else {
        message.error(response.message || (id ? '更新失败' : '创建失败'));
      }
    } catch (error) {
      message.error(error.message || (id ? '更新失败' : '创建失败'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card 
      title={id ? '编辑弹窗' : '新建弹窗'}
      extra={
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => navigate('/popups')}
        >
          返回列表
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          frequency_type: 'always',
          link_type: 'none',
          status: true,
          priority: 0,
        }}
      >
        <Form.Item
          label="弹窗标题"
          name="title"
          rules={[{ required: true, message: '请输入弹窗标题' }]}
        >
          <Input placeholder="请输入弹窗标题" />
        </Form.Item>

        <Form.Item
          label="图片地址"
          name="image_url"
          extra="图片地址和文字内容至少填写一项"
        >
          <ImageUpload placeholder="上传弹窗图片" />
        </Form.Item>

        <Form.Item
          label="文字内容"
          name="content"
          extra="无图片时显示此内容"
        >
          <TextArea rows={4} placeholder="请输入文字内容" />
        </Form.Item>

        <Form.Item
          label="时间范围"
          name="time_range"
          rules={[{ required: true, message: '请选择时间范围' }]}
        >
          <RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="频率类型"
          name="frequency_type"
          rules={[{ required: true, message: '请选择频率类型' }]}
        >
          <Radio.Group>
            <Radio value="always">每次都弹</Radio>
            <Radio value="total">总共次数</Radio>
            <Radio value="daily">每天次数</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.frequency_type !== currentValues.frequency_type
          }
        >
          {({ getFieldValue }) =>
            ['total', 'daily'].includes(getFieldValue('frequency_type')) ? (
              <Form.Item
                label="频率限制"
                name="frequency_limit"
                rules={[{ required: true, message: '请输入频率限制次数' }]}
              >
                <InputNumber min={1} placeholder="请输入次数" style={{ width: '100%' }} />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          label="条件字段"
          name="condition_field"
          extra="用于APP端判断，例如：is_vip"
        >
          <Input placeholder="请输入条件字段名" />
        </Form.Item>

        <Form.Item
          label="条件值"
          name="condition_value"
          extra="当用户该字段值等于此值时不再弹窗"
        >
          <Input placeholder="请输入条件值" />
        </Form.Item>

        <Form.Item
          label="链接类型"
          name="link_type"
          rules={[{ required: true, message: '请选择链接类型' }]}
        >
          <Radio.Group>
            <Radio value="none">无链接</Radio>
            <Radio value="internal">内部链接</Radio>
            <Radio value="external">外部链接</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.link_type !== currentValues.link_type
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('link_type') !== 'none' ? (
              <Form.Item
                label="链接地址"
                name="link_url"
                rules={[{ required: true, message: '请输入链接地址' }]}
              >
                <Input placeholder="请输入链接地址" />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          label="优先级"
          name="priority"
          extra="数值越大优先级越高"
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="状态"
          name="status"
          valuePropName="checked"
        >
          <Switch checkedChildren="启用" unCheckedChildren="禁用" defaultChecked />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? '更新' : '创建'}
            </Button>
            <Button onClick={() => navigate('/popups')}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default PopupForm;
